import { useState } from "react";
import { Plus, Phone, HelpCircle, ArrowUpRight, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const NAVY = "#081438";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const PAPER = "#FFFEFB";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FAQS = [{
  q: "Can I replace a garage door spring myself?",
  a: "We strongly advise against it. Torsion and extension springs hold enormous stored energy — a broken spring can release with force equivalent to a small explosion. DIY spring work causes hundreds of serious injuries in Australia every year. Licensed installers use specialised winding bars, safety cables, and tension-controlled procedures that aren't available at hardware stores. The savings aren't worth the risk.",
  related: {
    label: "Our spring replacement process",
    href: "#spring-process-heading"
  }
}, {
  q: "Should you replace one spring or both?",
  a: "Always both, on dual-spring doors. If one spring failed at 10,000 cycles, the other is at roughly 9,500 — it will fail within months, often weeks. Replacing as a matched pair costs slightly more upfront but saves you a second callout, second labour charge, and the risk of being stuck again. It's industry best practice — we never quote single-spring replacement on a dual-spring door.",
  related: {
    label: "Why matched-pair is our standard",
    href: "#why-choose-spring-heading"
  }
}, {
  q: "How long do garage door springs last?",
  a: "Standard 10,000-cycle springs last roughly 7 years for an average household (4 door cycles per day). High-cycle 20,000-cycle springs — which we fit as standard — last 12–15+ years under the same use. If you use your garage as the main entry to your home and cycle the door 6–8 times daily, expect 4–5 years on standard and 8–10 years on high-cycle.",
  related: null
}, {
  q: "What's the difference between torsion and extension springs?",
  a: "Torsion springs sit horizontally above the door, twisting to store energy. They're stronger, safer when broken, and used on most modern sectional doors. Extension springs sit vertically along the side tracks, stretching to store energy. They're older technology, common on tilt and older sectional doors, and more dangerous when broken because they can snap loose and become a projectile. We service both — most installs we do today are torsion.",
  related: null
}, {
  q: "My door is too heavy to lift after a snap — should I force it?",
  a: "No. Once a spring breaks, the door's counter-balance is gone and the full weight (often 80–150 kg) rests on the opener, cables, and tracks. Forcing it open by hand can damage cables, drums, and rollers — adding hundreds to your repair bill. Worse, the door can suddenly drop and cause injury. Leave it where it is, park the car somewhere else if needed, and call us. We'll guide you safely on the phone.",
  related: null
}, {
  q: "How quickly can you get out to me for a broken spring?",
  a: "Same day in most cases — we treat snapped springs as priority callouts. Typical response from call to van arrival is 2–3 hours within Sydney metro, faster in the inner suburbs. If you call before 10am, we'll usually have you fixed by lunchtime. After-hours emergency service is available if your door is stuck open and security is a concern.",
  related: {
    label: "Our 4-step process",
    href: "#spring-process-heading"
  }
}, {
  q: "Are your spring replacements warranted?",
  a: "Yes — every spring replacement comes with a 12-month written workmanship warranty plus the manufacturer's warranty on the springs themselves (typically 12 months on standard, 36 months on our high-cycle units). You receive a physical warranty card with our direct number — not just an emailed receipt. If anything fails inside warranty, we come back free of charge.",
  related: {
    label: "Our pricing pledges",
    href: "#spring-pricing-heading"
  }
}];
export default function SpringFaq() {
  const [openValue, setOpenValue] = useState<string>("item-0");
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="spring-faq-heading">
      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map(f => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a
          }
        }))
      })
    }} />

      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div aria-hidden className="absolute -top-32 -right-32 w-[450px] h-[450px] rounded-full pointer-events-none opacity-[0.05]" style={{
      backgroundColor: GOLD,
      filter: "blur(80px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        <div className="flex items-center justify-between gap-6 mb-12 md:mb-16 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD_DARK}66`,
            backgroundColor: `${GOLD}1A`
          }}>
              <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD_DARK
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD_DARK
          }}>
              Spring Replacement · FAQ
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${NAVY}26`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED
        }}>
            {FAQS.length} Questions · Real Answers
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* LEFT — Sticky */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <h2 id="spring-faq-heading" className="font-extrabold" style={{
            fontFamily: FONT_HEAD,
            color: NAVY,
            fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.03em"
          }}>
              The spring questions
              <br />
              we hear{" "}
              <span style={{
              color: GOLD_DARK
            }}>most often.</span>
            </h2>

            <p className="mt-6 text-[16px] leading-relaxed max-w-md" style={{
            color: INK_MUTED
          }}>
              Straight answers from a family-run shop that has replaced
              thousands of springs across Sydney. If your question isn&rsquo;t
              here, a 60-second phone call usually solves it.
            </p>

            <div className="mt-8 relative rounded-2xl border overflow-hidden p-5" style={{
            backgroundColor: PAPER,
            borderColor: `${NAVY}14`,
            boxShadow: `0 8px 28px -16px ${NAVY}26`
          }}>
              <div aria-hidden className="absolute top-0 left-5 right-5 h-px" style={{
              background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}40 100%)`
            }} />

              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-xl" style={{
                backgroundColor: NAVY,
                color: GOLD
              }}>
                  <MessageCircle className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-1" style={{
                  color: GOLD_DARK
                }}>
                    Question not listed?
                  </div>
                  <p className="text-[14px] leading-snug font-medium mb-3" style={{
                  color: INK
                }}>
                    Tell us your door brand and what you heard or saw &mdash;
                    we&rsquo;ll diagnose it on the phone before we leave the workshop.
                  </p>
                  <a href="tel:0485000933" className="group inline-flex items-center gap-2 text-[13px] font-bold transition-colors" style={{
                  color: NAVY,
                  fontFamily: FONT_BODY
                }} onMouseEnter={e => e.currentTarget.style.color = GOLD_DARK} onMouseLeave={e => e.currentTarget.style.color = NAVY}>
                    <Phone className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Call 0485 000 933
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Accordion */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={setOpenValue} className="w-full">
              {FAQS.map((faq, idx) => {
              const value = `item-${idx}`;
              const isOpen = openValue === value;
              const num = String(idx + 1).padStart(2, "0");
              return <AccordionItem key={value} value={value} className="border-0 mb-3">
                    <div className="rounded-2xl border overflow-hidden transition-all duration-300" style={{
                  backgroundColor: isOpen ? PAPER : "transparent",
                  borderColor: isOpen ? `${GOLD_DARK}40` : `${NAVY}14`,
                  boxShadow: isOpen ? `0 20px 50px -20px ${NAVY}26` : "none"
                }}>
                      <AccordionTrigger className="group w-full px-5 md:px-6 py-5 md:py-6 hover:no-underline [&[data-state=open]>div>span.chev]:rotate-45 [&[data-state=open]>div>span.chev]:bg-[#F5A623] [&[data-state=open]>div>span.chev>svg]:text-white [&>svg]:hidden">
                        <div className="flex items-center gap-4 md:gap-5 w-full text-left">
                          <span className="flex-shrink-0 font-extrabold leading-none transition-colors duration-300" style={{
                        fontFamily: FONT_HEAD,
                        color: isOpen ? GOLD_DARK : `${GOLD_DARK}40`,
                        fontSize: "1.5rem",
                        letterSpacing: "-0.02em",
                        fontVariantNumeric: "tabular-nums",
                        minWidth: "2.2ch"
                      }}>
                            {num}
                          </span>

                          <span className="flex-1 min-w-0 font-bold text-left transition-colors duration-200" style={{
                        fontFamily: FONT_HEAD,
                        color: isOpen ? NAVY : INK,
                        fontSize: "clamp(0.95rem, 1.3vw, 1.0625rem)",
                        lineHeight: 1.35,
                        letterSpacing: "-0.012em"
                      }}>
                            {faq.q}
                          </span>

                          <span className="chev flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-300" style={{
                        borderColor: isOpen ? GOLD_DARK : `${NAVY}26`,
                        backgroundColor: isOpen ? GOLD : "transparent"
                      }}>
                            <Plus className="h-4 w-4 transition-colors" strokeWidth={2.5} style={{
                          color: isOpen ? "white" : NAVY as React.CSSProperties["color"]
                        }} />
                          </span>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-5 md:px-6 pb-6">
                        <div className="ml-0 md:ml-[3.7rem] pt-1">
                          <div aria-hidden className="h-px mb-5 max-w-md" style={{
                        background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}26 100%)`
                      }} />

                          <p className="text-[14.5px] md:text-[15px] leading-[1.7]" style={{
                        color: INK,
                        fontFamily: FONT_BODY
                      }}>
                            {faq.a}
                          </p>

                          {faq.related && <a href={faq.related.href} className="group mt-4 inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.14em] uppercase transition-colors" style={{
                        color: GOLD_DARK,
                        fontFamily: FONT_BODY
                      }} onMouseEnter={e => e.currentTarget.style.color = NAVY} onMouseLeave={e => e.currentTarget.style.color = GOLD_DARK}>
                              <span className="inline-block w-4 h-px" style={{
                          backgroundColor: "currentColor"
                        }} />
                              {faq.related.label}
                              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
                            </a>}
                        </div>
                      </AccordionContent>
                    </div>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}